import type { ComponentProps, ReactNode } from 'react'
import type { HexAlphaColorPicker } from 'react-colorful'
import type {
  ControllerProps,
  FieldPath,
  FieldValues,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form'
import { Controller } from 'react-hook-form'
import ColorPicker from './color-picker'

type ColorPickerProps = ComponentProps<typeof HexAlphaColorPicker>

type ColorPickerRhfProps<TValues extends FieldValues, TName extends FieldPath<TValues>> = Omit<
  ColorPickerProps,
  'onChange' | 'color'
> &
  Omit<ControllerProps<TValues, TName>, 'render'> & {
    setValue: UseFormSetValue<TValues>
    afterChange?: (value: PathValue<TValues, TName>) => void
  }

export default function ColorPickerRhf<
  TValues extends FieldValues,
  TName extends FieldPath<TValues>,
>({
  control,
  name,
  rules,
  setValue,
  afterChange,
  ...restProps
}: ColorPickerRhfProps<TValues, TName>): ReactNode {
  const handleChange = (value: string): void => {
    const typedValue = value as PathValue<TValues, TName>
    setValue(name, typedValue)
    afterChange?.(typedValue)
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <ColorPicker {...restProps} color={field.value} onChange={handleChange} />
      )}
    />
  )
}
